import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Stack,
  Box,
  Chip,
  alpha,
  LinearProgress,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

/**
 * Dialog para asignar un paciente a un PCP.
 * Compatible con la estructura de /api/pcp/pcps/{locationId}.
 */
export default function AssignPcpDialog({ open, onClose, patient, pcps, onAssign }) {
  const [selectedPcp, setSelectedPcp] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAssign = async () => {
    if (!selectedPcp || !patient) return;
    setLoading(true);
    try {
      await onAssign(patient, selectedPcp);
      setSelectedPcp(null);
    } catch {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPcp(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)", px: 3, py: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PersonAddIcon sx={{ color: "white" }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>
            Assign Patient to PCP
          </Typography>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          {patient && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.divider, 0.08) }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">PATIENT</Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {patient.memberFullName || patient.patientName || "N/A"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                CIP: {patient.cipId} {patient.age ? `• Age: ${patient.age}` : ""} {patient.gender ? `• ${patient.gender}` : ""}
              </Typography>
            </Box>
          )}

          <Autocomplete
            options={pcps || []}
            getOptionLabel={(opt) => `${opt.title || ""} ${opt.pcpName || ""}`.trim()}
            value={selectedPcp}
            onChange={(_, val) => setSelectedPcp(val)}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              const usagePct = option.maxPatient ? Math.round((option.currentLoad / option.maxPatient) * 100) : null;
              const barColor = usagePct >= 100 ? "#EF4444" : usagePct >= 90 ? "#F59E0B" : "#10B981";
              const isFull = option.available === 0;
              return (
                <li key={key} {...rest} style={{ opacity: isFull ? 0.5 : 1 }}>
                  <Stack sx={{ width: "100%" }} spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>
                        {option.title} {option.pcpName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.currentLoad}/{option.maxPatient || "∞"}
                      </Typography>
                    </Stack>
                    {usagePct !== null && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(usagePct, 100)}
                        sx={{ height: 4, borderRadius: 2, bgcolor: alpha("#000", 0.06), "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 2 } }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {option.centerName} • {option.available !== null ? `${option.available} available` : "No limit"}
                    </Typography>
                  </Stack>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select PCP" placeholder="Search PCP..." />
            )}
            size="small"
          />

          {selectedPcp && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">SELECTED PCP</Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {selectedPcp.title} {selectedPcp.pcpName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip label={`${selectedPcp.currentLoad} patients`} size="small" sx={{ height: 22, fontSize: "0.7rem" }} />
                <Chip
                  label={selectedPcp.available !== null ? `${selectedPcp.available} available` : "Unlimited"}
                  size="small"
                  sx={{ height: 22, fontSize: "0.7rem", bgcolor: "#D1FAE5", color: "#065F46" }}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedPcp || loading}
          sx={{
            borderRadius: 2, textTransform: "none", fontWeight: 600,
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
          }}
        >
          {loading ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
